import { Navigate, Route, Routes } from "react-router-dom"

import { message } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"
import { isUserAdmin } from "@/domain/user"
import { useUser } from "@/hooks/useUser"
import { AcrConfigurationPage } from "@/pages/IntegrationsPage/components/pages/AcrConfigurationPage"
import { BitbucketConfigurationPage } from "@/pages/IntegrationsPage/components/pages/BitbucketConfigurationPage"
import { DockerHubConfigurationPage } from "@/pages/IntegrationsPage/components/pages/DockerHubConfigurationPage"
import { EcrConfigurationPage } from "@/pages/IntegrationsPage/components/pages/EcrConfigurationPage"
import { GcrConfigurationPage } from "@/pages/IntegrationsPage/components/pages/GcrConfigurationPage"
import { GitHubConfigurationPage } from "@/pages/IntegrationsPage/components/pages/GitHubConfigurationPage"
import { GitLabConfigurationPage } from "@/pages/IntegrationsPage/components/pages/GitLabConfigurationPage"
import { JiraConfigurationPage } from "@/pages/IntegrationsPage/components/pages/JiraConfigurationPage"
import { PackageRegistryConfigurationPage } from "@/pages/IntegrationsPage/components/pages/PackageRegistryConfigurationPage"
import { SlackConfigurationPage } from "@/pages/IntegrationsPage/components/pages/SlackConfigurationPage"
import { TicketingConfigurationPage } from "@/pages/IntegrationsPage/components/pages/TicketingConfigurationPage"
import { WebhookConfigurationPage } from "@/pages/IntegrationsPage/components/pages/WebhookConfigurationPage"
import { AcrIntegrationPage } from "./components/pages/AcrIntegrationPage"
import { AsanaIntegrationPage } from "./components/pages/AsanaIntegrationPage"
import { AzureBoardsIntegrationPage } from "./components/pages/AzureBoardsIntegrationPage"
import { AzureConfigurationPage } from "./components/pages/AzureConfigurationPage"
import { AzureIntegrationPage } from "./components/pages/AzureIntegrationPage"
import { CommonIntegrationPage } from "./components/pages/CommonIntegrationPage"
import { ConnectIntegrationPage } from "./components/pages/ConnectIntegrationPage"
import { DockerHubIntegrationPage } from "./components/pages/DockerHubIntegrationPage"
import { GcrIntegrationPage } from "./components/pages/GcrIntegrationPage"
import { GitHubIntegrationPage } from "./components/pages/GitHubIntegrationPage"
import { GitlabIntegrationPage } from "./components/pages/GitLabIntegrationPage"
import { IntegrationPage } from "./components/pages/IntegrationPage"
import { ListIntegrationsPage } from "./components/pages/ListIntegrationsPage"
import { PackageRegistryIntegrationPage } from "./components/pages/PackageRegistryIntegrationPage"
import { SlackIntegrationPage } from "./components/pages/SlackIntegrationPage"
import { UnauthorizedIntegrationCallbackPage } from "./components/pages/UnauthorizedIntegrationCallbackPage"
import { WebhookIntegrationPage } from "./components/pages/WebhookIntegrationPage"

import type { IntegrationsPageProps } from "."

export const IntegrationsPage = (props: IntegrationsPageProps) => {
  const { authorizedUser } = props
  const { INTEGRATIONS } = NAMES_ROUTES
  const [messageApi, messageContextHolder] = message.useMessage()
  const { user } = useUser()

  return (
    <>
      {messageContextHolder}
      <Routes>
        {!authorizedUser && (
          <>
            <Route path="/" element={<UnauthorizedIntegrationCallbackPage />} />
          </>
        )}
        {authorizedUser && (
          <>
            <Route path="/" element={<ListIntegrationsPage />} />
            <Route
              path={`/${INTEGRATIONS.CALLBACK}`}
              element={<IntegrationPage />}
            />
            <Route
              path={`/${INTEGRATION_VENDORS.gitlab}`}
              element={<GitLabConfigurationPage />}
            />
            <Route
              path={`/${INTEGRATION_VENDORS.github}`}
              element={<GitHubConfigurationPage />}
            />
            <Route
              path={`/${INTEGRATION_VENDORS.webhook}`}
              element={<WebhookConfigurationPage />}
            />
            <Route
              path={`/${INTEGRATION_VENDORS.ecr}`}
              element={<EcrConfigurationPage />}
            />
            <Route
              path={`/${INTEGRATION_VENDORS.acr}`}
              element={<AcrConfigurationPage />}
            />
            <Route
              path={`/${INTEGRATION_VENDORS.azure}`}
              element={<AzureConfigurationPage />}
            />
            <Route
              path={`/${INTEGRATION_VENDORS.bitbucket}`}
              element={<BitbucketConfigurationPage />}
            />
            <Route
              path={`/${INTEGRATION_VENDORS.jira}`}
              element={<JiraConfigurationPage />}
            />
            <Route
              path={`/${INTEGRATION_VENDORS.packageRegistry}`}
              element={<PackageRegistryConfigurationPage />}
            />
            <Route
              path={`/${INTEGRATION_VENDORS.dockerHub}`}
              element={<DockerHubConfigurationPage />}
            />
            <Route
              path={`/${INTEGRATION_VENDORS.gcr}`}
              element={<GcrConfigurationPage />}
            />
            <Route
              path={`/${INTEGRATION_VENDORS.slack}`}
              element={<SlackConfigurationPage />}
            />
            <Route
              path={`/${INTEGRATION_VENDORS.azureBoards}`}
              element={
                <TicketingConfigurationPage
                  vendor={INTEGRATION_VENDORS.azureBoards}
                />
              }
            />
            <Route
              path={`/${INTEGRATION_VENDORS.asana}`}
              element={
                <TicketingConfigurationPage
                  vendor={INTEGRATION_VENDORS.asana}
                />
              }
            />
            <Route path="/:vendor" element={<CommonIntegrationPage />} />

            {isUserAdmin(user) && (
              <>
                <Route
                  path={`/${INTEGRATION_VENDORS.gitlab}/connect`}
                  element={<GitlabIntegrationPage messageApi={messageApi} />}
                />
                <Route
                  path={`/${INTEGRATION_VENDORS.azure}/connect`}
                  element={<AzureIntegrationPage messageApi={messageApi} />}
                />
                <Route
                  path={`/${INTEGRATION_VENDORS.acr}/connect`}
                  element={<AcrIntegrationPage />}
                />
                <Route
                  path={`/${INTEGRATION_VENDORS.webhook}/connect`}
                  element={<WebhookIntegrationPage />}
                />
                <Route
                  path={`/${INTEGRATION_VENDORS.github}/connect`}
                  element={<GitHubIntegrationPage />}
                />
                <Route
                  path={`/${INTEGRATION_VENDORS.packageRegistry}/connect`}
                  element={<PackageRegistryIntegrationPage />}
                />
                <Route
                  path={`/${INTEGRATION_VENDORS.dockerHub}/connect`}
                  element={<DockerHubIntegrationPage />}
                />
                <Route
                  path={`/${INTEGRATION_VENDORS.gcr}/connect`}
                  element={<GcrIntegrationPage />}
                />
                <Route
                  path={`/${INTEGRATION_VENDORS.slack}/connect`}
                  element={<SlackIntegrationPage />}
                />
                <Route
                  path={`/${INTEGRATION_VENDORS.azureBoards}/connect`}
                  element={<AzureBoardsIntegrationPage />}
                />
                <Route
                  path={`/${INTEGRATION_VENDORS.asana}/connect`}
                  element={<AsanaIntegrationPage />}
                />
                <Route
                  path="/:nameIntegration/connect"
                  element={<ConnectIntegrationPage messageApi={messageApi} />}
                />
              </>
            )}
            <Route
              path="*"
              element={<Navigate to={`/${NAMES_ROUTES.INTEGRATIONS.ROOT}`} />}
            />
          </>
        )}
      </Routes>
    </>
  )
}
